import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useRef, useState } from "react";
import { ChangePasswordDialog } from "./ChangePasswordDialog";
import { ChangeEmailDialog } from "./ChangeEmailDialog";
import { useAvatar, useProfile, useUpdateProfile, useUploadAvatar } from "./api";
import { useAuth } from "../../auth/useAuth";
import { useNotify } from "../../app/useNotify";
import { errorMessageFor } from "../../api/client";
import { userStatusColour, userStatusLabel } from "../../lib/labels";
import { usePageTitle } from "../../app/usePageTitle";

export function ProfilePage() {
  usePageTitle("Profile");
  const { principal } = useAuth();
  const userId = principal?.userId ?? "";
  const notify = useNotify();
  const profile = useProfile(userId);
  const update = useUpdateProfile(userId);
  const avatar = useAvatar(userId);
  const upload = useUploadAvatar(userId);
  const fileInput = useRef<HTMLInputElement>(null);

  // null until the user types; the fetched profile shows through until then.
  const [draft, setDraft] = useState<{ firstName: string; lastName: string } | null>(null);
  const firstName = draft?.firstName ?? profile.data?.firstName ?? "";
  const lastName = draft?.lastName ?? profile.data?.lastName ?? "";
  const editing = draft !== null;

  if (profile.isLoading) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  async function saveName() {
    try {
      await update.mutateAsync({ firstName, lastName });
      notify.success("Name updated");
      setDraft(null);
    } catch (error) {
      notify.error(errorMessageFor(error) ?? "Could not update your name.");
    }
  }

  return (
    <Box>
      <Paper sx={{ p: 3, maxWidth: 640 }}>
        <Stack direction="row" sx={{ alignItems: "center", gap: 3, mb: 3 }}>
          <Avatar src={avatar.data} sx={{ width: 72, height: 72 }} alt="" />
          <Box>
            <Button
              onClick={() => fileInput.current?.click()}
              disabled={upload.isPending}
              size="small"
            >
              Change photo
            </Button>
            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              hidden
              aria-label="Profile photo"
              onChange={async (event) => {
                const file = event.target.files?.[0];
                if (!file) return;
                try {
                  await upload.mutateAsync(file);
                  notify.success("Photo updated");
                } catch (error) {
                  notify.error(errorMessageFor(error) ?? "Could not update your photo.");
                } finally {
                  event.target.value = "";
                }
              }}
            />
          </Box>
        </Stack>

        <Stack direction="row" sx={{ gap: 2, mb: 2 }}>
          <TextField
            label="First name"
            value={firstName}
            onChange={(event) => setDraft({ firstName: event.target.value, lastName })}
            fullWidth
          />
          <TextField
            label="Last name"
            value={lastName}
            onChange={(event) => setDraft({ firstName, lastName: event.target.value })}
            fullWidth
          />
        </Stack>

        <Stack direction="row" sx={{ gap: 2, alignItems: "center", mb: 3 }}>
          <Button variant="contained" onClick={saveName} disabled={!editing || update.isPending}>
            Save name
          </Button>
          {editing && <Button onClick={() => setDraft(null)}>Cancel</Button>}
        </Stack>

        <Stack direction="row" sx={{ gap: 2, alignItems: "center", mb: 1 }}>
          <Typography sx={{ fontWeight: 600 }}>Email</Typography>
          <Typography>{profile.data?.email}</Typography>
          {profile.data?.status && (
            <Chip
              size="small"
              label={userStatusLabel(profile.data.status)}
              color={userStatusColour(profile.data.status)}
            />
          )}
        </Stack>

        <Stack direction="row" sx={{ gap: 2, mt: 3 }}>
          <ChangePasswordDialog />
          <ChangeEmailDialog />
        </Stack>
      </Paper>
    </Box>
  );
}
